import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	Output,
	ElementRef,
	OnInit,
	OnDestroy,
} from '@angular/core';
import {
	Carousel2ItemComponent,
	Carousel2Component,
	ImpressionArgsFactory,
	ImpressionTrackingBehaviourType,
	LoggingService,
	PromoImpressionArgs,
	TealiumEventEnum,
	PromoTileImpressionArgs,
	TrackImpressionDirective,
	FeatureService,
} from '@woolworthsnz/styleguide';
import { DynamicComponentDirective } from '../../directives/dynamic-component.directive';
import { NgFor } from '@angular/common';
import { TealiumUtagService } from '@woolworthsnz/analytics';
import { ContextResponse } from '@woolworthsnz/trader-api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export enum DynamicContentContainerDisplayType {
	single = 'single',
	dynamic = 'itemssize',
	fixed = 'fixedsize',
}

export enum DynamicContentContainerDisplayAlign {
	left = 'left',
	center = 'center',
	right = 'right',
}

export enum DynamicContentPromotionTile {
	WeeklyOffers = 'Home Page Weekly Offers',
	DiscoverMore = 'Home Page Discover More Article',
	Specials = 'Specials Hub Weekly Offers',
}

@Component({
	selector: 'dc-carousel2',
	templateUrl: './carousel2-dc.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [Carousel2Component, NgFor, Carousel2ItemComponent, DynamicComponentDirective, TrackImpressionDirective],
})
export class Carousel2DCComponent implements OnInit, OnDestroy {
	@Input() name: string;
	@Input() enableArrowNavigation = false;
	@Input() autoRotateInterval: number | string;
	@Input() displayType: DynamicContentContainerDisplayType = DynamicContentContainerDisplayType.fixed;
	@Input() columns = 4; // Original NumberInDisplay setting
	@Input() align: DynamicContentContainerDisplayAlign; // Original Align setting

	@Output() itemsVisible = new EventEmitter<any[]>();

	public impressionArgs: PromoImpressionArgs[] = [];
	public promoImpressionArgs: PromoTileImpressionArgs[] = [];
	public autoRotationCancelled = false;
	public isCartologyGA4EventTrackingEnabled = false;
	private _items: any[] = [];
	private clickListener: (event: MouseEvent) => void;

	constructor(
		private el: ElementRef,
		private loggingService: LoggingService,
		private tealiumUtagService: TealiumUtagService,
		private featureService: FeatureService
	) {
		this.featureService.isEnabled(ContextResponse.EnabledFeaturesEnum.HaystackEDRChanges).pipe(takeUntilDestroyed()).subscribe((isEnabled) => {
			this.isCartologyGA4EventTrackingEnabled = isEnabled;
		});
	}

	ngOnInit(): void {
		this.clickListener = this.handleParentClick.bind(this);
		this.el.nativeElement.addEventListener('click', this.clickListener, true);
	}

	ngOnDestroy(): void {
		if (this.clickListener) {
			this.el.nativeElement.removeEventListener('click', this.clickListener, true);
		}
	}

	handleParentClick(event: MouseEvent): void {
		const item = this.getClickedItem(event);
		if (item) {
			this.handlePromotionClick(item);
		}
	}

	getClickedItem(event: MouseEvent): any {
		const target = (event.target as HTMLElement).closest('[data-item-id]');
		const itemId = target ? target.getAttribute('data-item-id') : undefined;
		return itemId ? this._items.find((item) => item.id === parseInt(itemId, 10)) : undefined;
	}

	handlePromotionClick(item: any): void {
		if (this.isCartologyGA4EventTrackingEnabled && item) {
			const index = item.order + 1;
			this.tealiumUtagService.link(
				{
					event: 'select_promotion',
					tealium_event: TealiumEventEnum.SelectPromotion,
					ecommerce: {
						creative_name: this.getCreativeNameByType(this.name),
						creative_slot: index.toString(),
						promotion_id: item.id.toString(),
						promotion_name: item.name,
					},
				},
			);
		}
	}

	@Input()
	set items(value: any[] | string) {
		// Needs to be able to parse json when loaded in legacy
		if (typeof value === 'string') {
			try {
				this._items = JSON.parse(value);
			} catch {
				this.loggingService.trackEvent('UI:Carousel2Component:Items', {
					error: 'Carousel items not valid JSON string',
					value,
				});
			}
		} else {
			this._items = value;
		}

		this.impressionArgs = this.getPromoImpressionArgs(this._items);
		this.promoImpressionArgs = this.getHomePagePromoImpressionArgs(this._items);
	}

	get items(): any[] {
		return this._items;
	}

	public emitVisible({ from, to }: { from: number; to: number }): void {
		const visibleItems = this.items.slice(from, to);
		this.itemsVisible.emit(visibleItems);
	}

	public get impressionTrackingBehaviour(): ImpressionTrackingBehaviourType {
		return this.autoRotateInterval && !this.autoRotationCancelled
			? ImpressionTrackingBehaviourType.TrackOnEachIntersection
			: ImpressionTrackingBehaviourType.TrackOnFirstIntersectionOnly;
	}

	private getCreativeNameByType(name: string): string {
		if (Object.values(DynamicContentPromotionTile).includes(name as DynamicContentPromotionTile)) {
			return name;
		}
		return '';
	}

	private getHomePagePromoImpressionArgs(items: any[]): PromoTileImpressionArgs[] {
		if (this.isCartologyGA4EventTrackingEnabled && items) {
			const promoArgs: PromoTileImpressionArgs[] = items.map((item, index) => ({
				creativeName: this.getCreativeNameByType(this.name),
				creativeSlot: (index + 1).toString(),
				promotionId: item.id ? item.id.toString() : '',
				promotionName: item.name ?? '',
			}));

			promoArgs.forEach((item) => {
				this.tealiumUtagService.link(
					{
						event: 'view_promotion',
						tealium_event: 'view_promotion',
						ecommerce: {
							creativeName: item.creativeName,
							creativeSlot: item.creativeSlot,
							promotionId: item.promotionId,
							promotionName: item.promotionName,
						},
					},
					true
				);
			});
			return promoArgs;
		} else {
			return [];
		}
	}

	private getPromoImpressionArgs(items: any[]): PromoImpressionArgs[] {
		return items.map((item, index) =>
			ImpressionArgsFactory.createCarouselPromoImpressionArgs(item, this.name, index + 1)
		);
	}
}
